/**
 * Bowtie Custom Node Components
 * Extracted from BowtiePage.js for maintainability
 */
import { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle, Shield, Zap, Link, AlertCircle, MinusCircle,
  CheckCircle, Trash2, GripVertical, MoreVertical
} from 'lucide-react';

// ==================== THREAT NODE ====================
export const ThreatNode = ({ data, id }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data.onDragOver) {
      const isValidDrop = data.onDragOver(e, id, 'threat');
      setIsDragOver(isValidDrop);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (data.onDrop) {
      data.onDrop(e, id, 'threat');
    }
  };
  
  return (
    <div 
      className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 border-2 min-w-[180px] max-w-[200px] transition-all ${
        isDragOver ? 'border-emerald-400 scale-105 ring-2 ring-emerald-400/50' : 'border-orange-400'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-orange-300" />
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded bg-orange-800/50 flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <span className="text-white font-semibold text-sm truncate flex-1">{data.label}</span>
      </div>
      <div className="flex items-center gap-2">
        {data.category && (
          <Badge className="text-xs bg-orange-800/50 text-orange-100">{data.category}</Badge>
        )}
        {data.likelihood && (
          <Badge className="text-xs bg-orange-900/50 text-orange-200">{data.likelihood}</Badge>
        )}
      </div>
      {isDragOver && (
        <div className="mt-2 text-xs text-emerald-200 flex items-center gap-1">
          <Link className="w-3 h-3" />
          Drop to link control
        </div>
      )}
    </div>
  );
};

// ==================== TOP EVENT NODE ====================
export const TopEventNode = ({ data }) => (
  <div className="relative">
    <Handle type="target" position={Position.Left} className="w-3 h-3 bg-amber-300" />
    <Handle type="source" position={Position.Right} className="w-3 h-3 bg-amber-300" />
    <div className="w-48 h-32 bg-gradient-to-br from-amber-500 to-orange-600 shadow-2xl flex items-center justify-center border-4 border-amber-400 rounded-2xl">
      <div className="text-center p-2">
        <div className="flex justify-center mb-1">
          <AlertTriangle className="w-6 h-6 text-white" />
        </div>
        <span className="text-white font-bold text-sm block">{data.label}</span>
        <span className="text-amber-100 text-xs">Top Event</span>
      </div>
    </div>
  </div>
);

// ==================== CONSEQUENCE NODE ====================
export const ConsequenceNode = ({ data, id }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  const severityColors = {
    catastrophic: 'from-red-700 to-red-600 border-red-500',
    hazardous: 'from-orange-700 to-orange-600 border-orange-500',
    major: 'from-amber-700 to-amber-600 border-amber-500',
    minor: 'from-yellow-700 to-yellow-600 border-yellow-500',
    negligible: 'from-slate-600 to-slate-500 border-slate-400'
  };
  const colorClass = severityColors[data.severity] || severityColors.major;
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data.onDragOver) {
      const isValidDrop = data.onDragOver(e, id, 'consequence');
      setIsDragOver(isValidDrop);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (data.onDrop) {
      data.onDrop(e, id, 'consequence');
    }
  };
  
  return (
    <div 
      className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${colorClass} border-2 min-w-[180px] max-w-[200px] transition-all ${
        isDragOver ? 'border-emerald-400 scale-105 ring-2 ring-emerald-400/50' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-purple-300" />
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded bg-black/20 flex items-center justify-center">
          <AlertCircle className="w-4 h-4 text-white" />
        </div>
        <span className="text-white font-semibold text-sm truncate flex-1">{data.label}</span>
      </div>
      <div className="flex items-center gap-2">
        {data.severity && (
          <Badge className="text-xs bg-black/30 text-white capitalize">{data.severity}</Badge>
        )}
        {data.category && (
          <Badge className="text-xs bg-black/20 text-white/80">{data.category}</Badge>
        )}
      </div>
      {isDragOver && (
        <div className="mt-2 text-xs text-emerald-200 flex items-center gap-1">
          <Link className="w-3 h-3" />
          Drop to link control
        </div>
      )}
    </div>
  );
};

// ==================== BARRIER EFFECTIVENESS COLORS ====================
const effectivenessColors = {
  high: { bg: 'from-emerald-600 to-emerald-500', border: 'border-emerald-400', badge: 'bg-emerald-800', ring: 'ring-emerald-400' },
  medium: { bg: 'from-yellow-600 to-yellow-500', border: 'border-yellow-400', badge: 'bg-yellow-800', ring: 'ring-yellow-400' },
  low: { bg: 'from-orange-600 to-orange-500', border: 'border-orange-400', badge: 'bg-orange-800', ring: 'ring-orange-400' },
  ineffective: { bg: 'from-red-600 to-red-500', border: 'border-red-400', badge: 'bg-red-800', ring: 'ring-red-400' }
};

// ==================== BARRIER CONTEXT MENU ====================
const BarrierContextMenu = ({ effectiveness, onEffectivenessChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="p-0.5 rounded hover:bg-white/20 transition-colors" title="Change effectiveness">
        <MoreVertical className="w-3 h-3 text-white/80" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-slate-900 border-slate-700 min-w-[160px]">
      <DropdownMenuLabel className="text-slate-400 text-xs">Change Effectiveness</DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-slate-700" />
      <DropdownMenuItem 
        className={`text-emerald-400 focus:bg-emerald-900/50 focus:text-emerald-300 ${effectiveness === 'high' ? 'bg-emerald-900/30' : ''}`}
        onClick={() => onEffectivenessChange('high')}
      >
        <CheckCircle className="w-4 h-4 mr-2" />
        <span className="flex-1">Effective</span>
        <span className="text-slate-500 text-xs ml-2">1</span>
        {effectiveness === 'high' && <span className="ml-1">✓</span>}
      </DropdownMenuItem>
      <DropdownMenuItem 
        className={`text-yellow-400 focus:bg-yellow-900/50 focus:text-yellow-300 ${effectiveness === 'medium' ? 'bg-yellow-900/30' : ''}`}
        onClick={() => onEffectivenessChange('medium')}
      >
        <AlertCircle className="w-4 h-4 mr-2" />
        <span className="flex-1">Partial</span>
        <span className="text-slate-500 text-xs ml-2">2</span>
        {effectiveness === 'medium' && <span className="ml-1">✓</span>}
      </DropdownMenuItem>
      <DropdownMenuItem 
        className={`text-orange-400 focus:bg-orange-900/50 focus:text-orange-300 ${effectiveness === 'low' ? 'bg-orange-900/30' : ''}`}
        onClick={() => onEffectivenessChange('low')}
      >
        <MinusCircle className="w-4 h-4 mr-2" />
        <span className="flex-1">Weak</span>
        <span className="text-slate-500 text-xs ml-2">3</span>
        {effectiveness === 'low' && <span className="ml-1">✓</span>}
      </DropdownMenuItem>
      <DropdownMenuItem 
        className={`text-red-400 focus:bg-red-900/50 focus:text-red-300 ${effectiveness === 'ineffective' ? 'bg-red-900/30' : ''}`}
        onClick={() => onEffectivenessChange('ineffective')}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        <span className="flex-1">Non-effective</span>
        <span className="text-slate-500 text-xs ml-2">4</span>
        {effectiveness === 'ineffective' && <span className="ml-1">✓</span>}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// ==================== PREVENTIVE BARRIER NODE ====================
export const PreventiveBarrierNode = ({ data, id }) => {
  const [isDragging, setIsDragging] = useState(false);
  const isSelected = data.isSelected;
  const colors = effectivenessColors[data.effectiveness] || effectivenessColors.medium;
  
  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      barrierId: data.barrierId || id.replace('barrier-prev-', ''),
      barrierType: 'preventive',
      barrierName: data.label
    }));
    e.dataTransfer.effectAllowed = 'link';
    setIsDragging(true);
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (data.onSelect) {
      data.onSelect(data.barrierId || id.replace('barrier-prev-', ''));
    }
  };
  
  const handleEffectivenessChange = (newEffectiveness) => {
    if (data.onEffectivenessChange) {
      data.onEffectivenessChange(data.barrierId || id.replace('barrier-prev-', ''), newEffectiveness);
    }
  };
  
  const effectivenessLabel = data.effectiveness === 'high' ? 'Effective' : 
                             data.effectiveness === 'medium' ? 'Partial' :
                             data.effectiveness === 'low' ? 'Weak' : 'Non-effective';
  
  return (
    <div 
      className={`px-3 py-2 shadow-lg rounded-lg bg-gradient-to-r ${colors.bg} ${colors.border} border-2 min-w-[150px] max-w-[180px] cursor-grab transition-all ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${isSelected ? `ring-2 ${colors.ring} ring-offset-2 ring-offset-slate-900` : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      title="Drag to link to a threat • Click to select • Press 1-4 to change effectiveness"
    >
      <Handle type="target" position={Position.Left} className="w-2 h-2 bg-blue-300" />
      <Handle type="source" position={Position.Right} className="w-2 h-2 bg-blue-300" />
      <div className="flex items-center gap-1 mb-1">
        <GripVertical className="w-3 h-3 text-white/60 flex-shrink-0 cursor-move" title="Drag to link" />
        <Shield className="w-4 h-4 text-white flex-shrink-0" />
        <span className="text-white font-medium text-xs truncate flex-1">{data.label}</span>
        <BarrierContextMenu effectiveness={data.effectiveness} onEffectivenessChange={handleEffectivenessChange} />
      </div>
      <div className="flex items-center justify-between">
        <Badge className={`text-xs ${colors.badge} text-white capitalize`}>
          {effectivenessLabel}
        </Badge>
        <Badge className="text-xs bg-blue-700/80 text-blue-100">Preventive</Badge>
      </div>
      {data.linkedTo && (
        <div className="mt-1 text-xs text-blue-200 flex items-center gap-1">
          <Link className="w-3 h-3" />
          Linked
        </div>
      )}
      {isSelected && (
        <div className="mt-1 text-xs text-white/70 text-center">
          Press 1-4 to change
        </div>
      )}
    </div>
  );
};

// ==================== RECOVERY BARRIER NODE ====================
export const RecoveryBarrierNode = ({ data, id }) => {
  const [isDragging, setIsDragging] = useState(false);
  const isSelected = data.isSelected;
  const colors = effectivenessColors[data.effectiveness] || effectivenessColors.medium;
  
  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      barrierId: data.barrierId || id.replace('barrier-rec-', ''),
      barrierType: 'mitigating',
      barrierName: data.label
    }));
    e.dataTransfer.effectAllowed = 'link';
    setIsDragging(true);
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (data.onSelect) {
      data.onSelect(data.barrierId || id.replace('barrier-rec-', ''));
    }
  };
  
  const handleEffectivenessChange = (newEffectiveness) => {
    if (data.onEffectivenessChange) {
      data.onEffectivenessChange(data.barrierId || id.replace('barrier-rec-', ''), newEffectiveness);
    }
  };
  
  const effectivenessLabel = data.effectiveness === 'high' ? 'Effective' : 
                             data.effectiveness === 'medium' ? 'Partial' :
                             data.effectiveness === 'low' ? 'Weak' : 'Non-effective';
  
  return (
    <div 
      className={`px-3 py-2 shadow-lg rounded-lg bg-gradient-to-r ${colors.bg} ${colors.border} border-2 min-w-[150px] max-w-[180px] cursor-grab transition-all ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${isSelected ? `ring-2 ${colors.ring} ring-offset-2 ring-offset-slate-900` : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      title="Drag to link to a consequence • Click to select • Press 1-4 to change effectiveness"
    >
      <Handle type="target" position={Position.Left} className="w-2 h-2 bg-purple-300" />
      <Handle type="source" position={Position.Right} className="w-2 h-2 bg-purple-300" />
      <div className="flex items-center gap-1 mb-1">
        <GripVertical className="w-3 h-3 text-white/60 flex-shrink-0 cursor-move" title="Drag to link" />
        <Shield className="w-4 h-4 text-white flex-shrink-0" />
        <span className="text-white font-medium text-xs truncate flex-1">{data.label}</span>
        <BarrierContextMenu effectiveness={data.effectiveness} onEffectivenessChange={handleEffectivenessChange} />
      </div>
      <div className="flex items-center justify-between">
        <Badge className={`text-xs ${colors.badge} text-white capitalize`}>
          {effectivenessLabel}
        </Badge>
        <Badge className="text-xs bg-purple-700/80 text-purple-100">Recovery</Badge>
      </div>
      {data.linkedTo && (
        <div className="mt-1 text-xs text-purple-200 flex items-center gap-1">
          <Link className="w-3 h-3" />
          Linked
        </div>
      )}
      {isSelected && (
        <div className="mt-1 text-xs text-white/70 text-center">
          Press 1-4 to change
        </div>
      )}
    </div>
  );
};

// ==================== NODE TYPES EXPORT ====================
export const bowtieNodeTypes = {
  threat: ThreatNode,
  topEvent: TopEventNode,
  consequence: ConsequenceNode,
  preventiveBarrier: PreventiveBarrierNode,
  recoveryBarrier: RecoveryBarrierNode
};
